import {
  Drawer,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Box from "@mui/material/Box";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { gray50 } from "../../theme";
import { Header } from "../Header";
import { Icon } from "../Icon";
import { MainMenu } from "../MainMenu";
import { useRenderRightMenuContent } from "./RightMenu";

const openedMixin = ({ theme, width }) => ({
  width: width,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = ({ theme, width }) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: width || `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: width || `calc(${theme.spacing(8)} + 1px)`,
  },
});

export const StyledDrawer = styled(Drawer, {
  // forward `open` to the underlying Drawer so temporary variant works
  shouldForwardProp: (prop) => prop !== "toggleMobile",
})(({ theme, open, width, toggleMobile, variant, ...rest }) => {
  const isTemporary = variant === "temporary";
  return {
    width: width,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    position: "relative",
    "& .MuiDrawer-paper": {
      ...(toggleMobile ? { overflow: "visible" } : {}),
      boxSizing: "border-box",
      ...(isTemporary ? { width: width } : {}),
    },
    // permanent behavior (open/closed) only for non-temporary drawers
    ...(!isTemporary &&
      open && {
        ...openedMixin({ theme, width }),
        "& .MuiDrawer-paper": openedMixin({ theme, width }),
      }),
    ...(!isTemporary &&
      !open && {
        ...closedMixin({ theme, width }),
        "& .MuiDrawer-paper": closedMixin({ theme, width }),
      }),
  };
});

const SideMenu = ({
  children,
  width,
  anchor,
  toggleMobile,
  open,
  printHidden,
  variant,
  onClose,
}) => {
  const printSx = printHidden
    ? {
        "@media print": {
          display: "none",
        },
      }
    : {};

  return (
    <StyledDrawer
      // elevation={1} // TODO: discuss & overflow?
      id={`side-menu-${anchor}`}
      width={width}
      variant={variant || "permanent"}
      onClose={onClose}
      anchor={anchor}
      open={open}
      toggleMobile={toggleMobile}
      sx={printSx}
    >
      {children}
      {toggleMobile ? (
        <Box
          sx={{
            position: "absolute",
            ...(anchor === "left" ? { right: "-20px" } : { left: "-20px" }),
            top: "55px",
          }}
        >
          {toggleMobile}
        </Box>
      ) : null}
    </StyledDrawer>
  );
};

export const LayoutCtx = createContext({});

export const Layout = ({
  children,
  header,
  HeaderComponent = Header,
  rightMenuContent: rightMenuContentProp,
  printLeftMenuHidden = false,
  rightMenuOpenSinceBreakpoint = "xl",
  rightMenuPermanentSinceBreakpoint = "sm", // TODO: define per-page. sm is conservative, works e.g. for sessions
  sx = {},
}) => {
  const theme = useTheme();
  // up is inclusive, down is exclusive
  const rightMenuPermanent = useMediaQuery(
    theme.breakpoints.up(rightMenuPermanentSinceBreakpoint),
  );
  const lgAndUp = useMediaQuery(theme.breakpoints.up("lg"));

  const [leftOpen, setLeftOpen] = useState(lgAndUp);
  useEffect(() => {
    setLeftOpen(lgAndUp);
  }, [lgAndUp]);
  const handleToggleLeft = useCallback(() => {
    setLeftOpen((leftOpen) => !leftOpen);
  }, []);

  const rightMenuOpenReactive = useMediaQuery(
    theme.breakpoints.up(rightMenuOpenSinceBreakpoint),
  );
  const [hasRightMenuToggleIcon, setHasRightMenuToggleIcon] = useState();
  const [rightOpen, setRightOpen] = useState(rightMenuOpenReactive);
  useEffect(() => {
    setRightOpen(rightMenuOpenReactive);
  }, [rightMenuOpenReactive]);
  const handleToggleRight = useCallback(() => {
    setRightOpen((rightOpen) => !rightOpen);
  }, []);

  const renderRightMenuContent = useRenderRightMenuContent();
  const rightMenuContent = useMemo(() => {
    return (
      rightMenuContentProp ||
      renderRightMenuContent?.({
        rightOpen,
        setRightOpen,
        hasRightMenuToggleIcon,
        setHasRightMenuToggleIcon,
      })
    );
  }, [
    hasRightMenuToggleIcon,
    renderRightMenuContent,
    rightMenuContentProp,
    rightOpen,
  ]);
  const hasRightMenuContent = !!rightMenuContent;
  useEffect(
    () => setHasRightMenuToggleIcon(hasRightMenuContent),
    [hasRightMenuContent],
  );
  const leftWidth = leftOpen ? 256 : 88;
  const rightVariant = rightMenuPermanent ? "permanent" : "temporary";
  const rightMenuWidth = lgAndUp ? 392 : 300;

  return (
    <LayoutCtx.Provider value={{ rightOpen }}>
      <Box
        id="layout"
        sx={{
          display: "flex",
          bgcolor: gray50,
          minHeight: "100%",
          overflow: "auto",
        }}
      >
        <SideMenu
          width={leftWidth}
          anchor="left"
          open={leftOpen}
          printHidden={printLeftMenuHidden}
          toggleMobile={
            // TODO: move icon
            lgAndUp ? null : (
              <IconButton
                variant="outlined"
                size="small"
                disableRipple
                sx={{ backgroundColor: "white", border: "1px solid #EAECF0" }}
                onClick={handleToggleLeft}
              >
                <Icon name={leftOpen ? "ArrowBack" : "ArrowForward"} />
              </IconButton>
            )
          }
        >
          <MainMenu open={leftOpen} />
        </SideMenu>
        <Box
          component="main"
          sx={{
            px: 3,
            pb: 3, // TODO: check everywhere
            overflow: "auto",
            width: "100%",
            flexGrow: 1,
            position: "relative",
            ...sx,
          }}
        >
          <HeaderComponent
            {...header}
            toggleRightMenuIcon={
              hasRightMenuToggleIcon && (
                <IconButton variant="outlined" onClick={handleToggleRight}>
                  <Icon
                    name={"MenuOpenOutlined"}
                    sx={{
                      transform: rightOpen ? "rotate(0.5turn)" : undefined,
                    }}
                  />
                </IconButton>
              )
            }
          />
          {children}
        </Box>

        {rightMenuContent && (
          <SideMenu
            width={
              rightVariant === "temporary"
                ? rightMenuWidth
                : rightOpen
                  ? rightMenuWidth
                  : 12
            }
            anchor="right"
            open={rightOpen}
            variant={rightVariant}
            onClose={
              rightVariant === "temporary"
                ? () => setRightOpen(false)
                : undefined
            }
          >
            {rightMenuContent}
          </SideMenu>
        )}
      </Box>
    </LayoutCtx.Provider>
  );
};
